import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import {
  BILL_PDF_UPLOAD_SESSION_PATH as SESSION_PATH,
  requestBillPdfUploadSession,
} from "@/modules/data/utils/bill-pdf-upload-session-request"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import { useQueryKey } from "@/services/utils/useQueryKey"
import type { HttpError, HttpErrorRecord } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { snakeCaseToCamelCase } from "@/utils/formatters"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import type {
  BillPdfConfirmResponse,
  BillPdfPreviewRow,
  BillPdfPreviewRowEdit,
  BillPdfUploadSession,
} from "./bill-pdf-upload-session.types"

const shouldPollActiveSession = (
  status: BillPdfUploadSession["status"] | undefined
): number | false =>
  status === "parsing" || status === "importing" ? 2500 : false

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const isBillPdfUploadSession = (
  value: unknown
): value is BillPdfUploadSession => {
  if (!isRecord(value)) {
    return false
  }

  return (
    typeof value.id === "string" &&
    typeof value.status === "string" &&
    Array.isArray(value.files) &&
    Array.isArray(value.rows)
  )
}

const sessionFromResponse = (payload: unknown): BillPdfUploadSession | null => {
  if (!isRecord(payload)) {
    return null
  }

  const { data } = payload
  if (data == null) {
    return null
  }

  const session = snakeCaseToCamelCase(data)
  return isBillPdfUploadSession(session) ? session : null
}

const confirmMessageFromResponse = (payload: unknown): string => {
  if (!isRecord(payload) || !isRecord(payload.data)) {
    return ""
  }

  const { message } = payload.data
  return typeof message === "string" ? message : ""
}

const fetchActiveSession = async (
  organizationId: Organization["id"]
): Promise<BillPdfUploadSession | null> =>
  requestBillPdfUploadSession(async () => {
    const response = await httpClient.get(SESSION_PATH + "/active", {
      params: { organization_id: organizationId },
    })
    return sessionFromResponse(response.data)
  })

const createSession = async ({
  files,
  organizationId,
}: {
  files: File[]
  organizationId: Organization["id"]
}): Promise<BillPdfUploadSession> =>
  requestBillPdfUploadSession(async () => {
    const formData = new FormData()
    formData.append("organization_id", organizationId ?? "")
    files.forEach((file) => {
      formData.append("files[]", file)
    })

    const response = await httpClient.post(SESSION_PATH, formData)
    const session = sessionFromResponse(response.data)
    if (!session) {
      throw new Error("Bill PDF upload session was not created")
    }

    return session
  })

const confirmSession = async ({
  organizationId,
  sessionId,
}: {
  organizationId: Organization["id"]
  sessionId: string
}): Promise<BillPdfConfirmResponse> =>
  requestBillPdfUploadSession(async () => {
    const response = await httpClient.post(
      `${SESSION_PATH}/${sessionId}/confirm`,
      null,
      { params: { organization_id: organizationId } }
    )
    return { message: confirmMessageFromResponse(response.data) }
  })

const cancelSession = async ({
  organizationId,
  sessionId,
}: {
  organizationId: Organization["id"]
  sessionId: string
}): Promise<undefined> => {
  try {
    await httpClient.delete(`${SESSION_PATH}/${sessionId}`, {
      params: { organization_id: organizationId },
    })
    return undefined
  } catch (error: unknown) {
    const httpError = toHttpError(error)
    if (httpError.status === 404) {
      return undefined
    }

    throw httpError
  }
}

const removeSessionRow = async ({
  organizationId,
  rowId,
  sessionId,
}: {
  organizationId: Organization["id"]
  rowId: string
  sessionId: string
}): Promise<undefined> =>
  requestBillPdfUploadSession(async () => {
    await httpClient.delete(`${SESSION_PATH}/${sessionId}/rows/${rowId}`, {
      params: { organization_id: organizationId },
    })
    return undefined
  })

const toPreviewRow = (payload: unknown): BillPdfPreviewRow => {
  const row = isRecord(payload) ? snakeCaseToCamelCase(payload.data) : null
  if (!isRecord(row) || typeof row.id !== "string") {
    throw new Error("Bill PDF preview row was not returned")
  }

  return row as unknown as BillPdfPreviewRow
}

const updateSessionRow = async ({
  edit,
  organizationId,
  rowId,
  sessionId,
}: {
  edit: BillPdfPreviewRowEdit
  organizationId: Organization["id"]
  rowId: string
  sessionId: string
}): Promise<BillPdfPreviewRow> =>
  requestBillPdfUploadSession(async () => {
    const response = await httpClient.patch(
      `${SESSION_PATH}/${sessionId}/rows/${rowId}`,
      {
        cost: edit.cost,
        currency: edit.currency,
        end_date: edit.endDate,
        meter_id: edit.meterId,
        resource_type: edit.resourceType,
        site_id: edit.siteId,
        start_date: edit.startDate,
        usage: edit.usage,
        usage_unit: edit.usageUnit,
        utility_provider: edit.utilityProvider,
      },
      { params: { organization_id: organizationId } }
    )
    return toPreviewRow(response.data)
  })

const replacePreviewRow = (
  session: BillPdfUploadSession | null | undefined,
  row: BillPdfPreviewRow
): BillPdfUploadSession | null | undefined => {
  if (!session) {
    return session
  }

  return {
    ...session,
    rows: session.rows.map((candidate) =>
      candidate.id === row.id ? row : candidate
    ),
  }
}

const omitPreviewRow = (
  session: BillPdfUploadSession | null | undefined,
  rowId: string
): BillPdfUploadSession | null | undefined => {
  if (!session) {
    return session
  }

  return {
    ...session,
    rows: session.rows.filter((row) => row.id !== rowId),
  }
}

const restorePreviewRow = (
  current: BillPdfUploadSession | null | undefined,
  previous: BillPdfUploadSession | null | undefined,
  rowId: string
): BillPdfUploadSession | null | undefined => {
  if (!previous) {
    return current
  }

  if (current?.id !== previous.id) {
    return current
  }

  const removedRow = previous.rows.find((row) => row.id === rowId)
  if (!removedRow || current.rows.some((row) => row.id === rowId)) {
    return current
  }

  const originalIndex = previous.rows.findIndex((row) => row.id === rowId)
  const rows = [...current.rows]
  const insertAt =
    originalIndex === -1 ? rows.length : Math.min(originalIndex, rows.length)
  rows.splice(insertAt, 0, removedRow)

  return { ...current, rows }
}

const REMOVE_ROW_MUTATION_KEY = createQueryKey(
  ApiQueryName.RemoveBillPdfUploadSessionRow,
  "deleteOne"
)

const UPDATE_ROW_MUTATION_KEY = createQueryKey(
  ApiQueryName.UpdateBillPdfUploadSessionRow,
  "updateOne"
)

interface UseBillPdfUploadSessionOptions {
  enabled?: boolean
  organizationId?: Organization["id"]
}

export const useBillPdfUploadSession = ({
  enabled = true,
  organizationId,
}: UseBillPdfUploadSessionOptions = {}) => {
  const { t } = useTranslation("data")
  const { setNotification } = useNotification()
  const queryClient = useQueryClient()
  const { invalidateQueries } = useInvalidateQueries()
  const removePendingCount = useIsMutating({
    mutationKey: REMOVE_ROW_MUTATION_KEY,
  })
  const updatePendingCount = useIsMutating({
    mutationKey: UPDATE_ROW_MUTATION_KEY,
  })
  // Read straight from the mutation cache where the answer is needed before
  // the next render, e.g. in the same tick a row removal starts.
  const isRowMutationInFlight = (): boolean =>
    queryClient.isMutating({ mutationKey: REMOVE_ROW_MUTATION_KEY }) > 0 ||
    queryClient.isMutating({ mutationKey: UPDATE_ROW_MUTATION_KEY }) > 0
  const queryKey = useQueryKey(
    ApiQueryName.BillPdfUploadSession,
    "getOne",
    "active",
    { organizationId }
  )

  const queryResult = useQuery<
    BillPdfUploadSession | null,
    HttpError<HttpErrorRecord>
  >({
    enabled: Boolean(organizationId) && enabled,
    queryFn: () => fetchActiveSession(organizationId ?? ""),
    queryKey,
    refetchInterval: (query) =>
      isRowMutationInFlight()
        ? false
        : shouldPollActiveSession(query.state.data?.status),
    refetchOnMount: () => (isRowMutationInFlight() ? false : "always"),
    refetchOnReconnect: () => !isRowMutationInFlight(),
    refetchOnWindowFocus: () => !isRowMutationInFlight(),
  })

  const invalidateSession = () => {
    void queryClient.invalidateQueries({ queryKey })
  }

  const createMutation = useMutation<
    BillPdfUploadSession,
    HttpError<HttpErrorRecord>,
    { files: File[]; organizationId: Organization["id"] }
  >({
    meta: { handlesOwnErrors: true },
    mutationFn: (variables) => createSession(variables),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
      queryClient.setQueryData(queryKey, null)
    },
    onSettled: () => {
      invalidateSession()
    },
    onSuccess: (session) => {
      queryClient.setQueryData(queryKey, session)
    },
  })

  const confirmMutation = useMutation<
    BillPdfConfirmResponse,
    HttpError<HttpErrorRecord>,
    { organizationId: Organization["id"]; sessionId: string }
  >({
    meta: { handlesOwnErrors: true },
    mutationFn: (variables) => confirmSession(variables),
    onSuccess: () => {
      queryClient.setQueryData(queryKey, null)
    },
    onSettled: () => {
      invalidateSession()
    },
  })

  const cancelMutation = useMutation<
    undefined,
    HttpError<HttpErrorRecord>,
    { organizationId: Organization["id"]; sessionId: string }
  >({
    meta: { handlesOwnErrors: true },
    mutationFn: (variables) => cancelSession(variables),
    onSuccess: () => {
      queryClient.setQueryData(queryKey, null)
      invalidateSession()
    },
  })

  const removeMutation = useMutation<
    undefined,
    HttpError<HttpErrorRecord>,
    {
      organizationId: Organization["id"]
      rowId: string
      sessionId: string
    },
    { previous: BillPdfUploadSession | null }
  >({
    meta: { handlesOwnErrors: true },
    mutationFn: (variables) => removeSessionRow(variables),
    mutationKey: REMOVE_ROW_MUTATION_KEY,
    onError: (error, { rowId }, context) => {
      queryClient.setQueryData<BillPdfUploadSession | null>(
        queryKey,
        (current) =>
          restorePreviewRow(current, context?.previous, rowId) ?? null
      )
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("upload.billPdf.removeFailed")
        ),
        type: "error",
      })
    },
    onMutate: async ({ rowId }) => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<BillPdfUploadSession | null>(queryKey) ?? null
      queryClient.setQueryData<BillPdfUploadSession | null>(
        queryKey,
        (current) => omitPreviewRow(current, rowId) ?? null
      )
      return { previous }
    },
    onSuccess: (_data, { rowId }) => {
      queryClient.setQueryData<BillPdfUploadSession | null>(
        queryKey,
        (current) => omitPreviewRow(current, rowId) ?? null
      )
      invalidateQueries([
        createQueryKey(ApiQueryName.BillPdfUploadSessionAnalysis, "getOne"),
      ])
    },
  })

  const updateMutation = useMutation<
    BillPdfPreviewRow,
    HttpError<HttpErrorRecord>,
    {
      edit: BillPdfPreviewRowEdit
      organizationId: Organization["id"]
      rowId: string
      sessionId: string
    }
  >({
    meta: { handlesOwnErrors: true },
    mutationFn: (variables) => updateSessionRow(variables),
    mutationKey: UPDATE_ROW_MUTATION_KEY,
    onError: (error) => {
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("upload.billPdf.updateFailed")
        ),
        type: "error",
      })
    },
    // A session fetch already in flight can still finish after the PATCH and
    // put the pre-edit row back. Later refetches are already blocked.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
    },
    onSuccess: (row) => {
      queryClient.setQueryData<BillPdfUploadSession | null>(
        queryKey,
        (current) => replacePreviewRow(current, row) ?? null
      )
      invalidateQueries([
        createQueryKey(ApiQueryName.BillPdfUploadSessionAnalysis, "getOne"),
      ])
    },
  })

  return {
    ...useRenameQueryResult(queryResult, ApiQueryName.BillPdfUploadSession),
    ...useRenameQueryResult(
      cancelMutation,
      ApiQueryName.CancelBillPdfUploadSession
    ),
    ...useRenameQueryResult(
      confirmMutation,
      ApiQueryName.ConfirmBillPdfUploadSession
    ),
    ...useRenameQueryResult(
      createMutation,
      ApiQueryName.CreateBillPdfUploadSession
    ),
    ...useRenameQueryResult(
      removeMutation,
      ApiQueryName.RemoveBillPdfUploadSessionRow
    ),
    removeBillPdfUploadSessionRowIsPending: removePendingCount > 0,
    updateBillPdfUploadSessionRowIsPending: updatePendingCount > 0,
    cancelBillPdfUploadSession: (sessionId: string) => {
      if (!organizationId) {
        return
      }

      cancelMutation.mutate({ organizationId, sessionId })
    },
    confirmBillPdfUploadSession: (sessionId: string) => {
      if (!organizationId || isRowMutationInFlight()) {
        return
      }

      confirmMutation.mutate({ organizationId, sessionId })
    },
    createBillPdfUploadSession: (files: File[]) => {
      if (!organizationId) {
        return
      }

      createMutation.mutate({ files, organizationId })
    },
    removeBillPdfPreviewRow: (rowId: string) => {
      if (!organizationId) {
        return
      }

      const sessionId = queryClient.getQueryData<BillPdfUploadSession | null>(
        queryKey
      )?.id
      if (!sessionId) {
        return
      }

      removeMutation.mutate({ organizationId, rowId, sessionId })
    },
    // Resolves once the row is saved and rejects when the save fails, so the
    // edit dialog can stay open on failure.
    updateBillPdfPreviewRow: async (
      rowId: string,
      edit: BillPdfPreviewRowEdit
    ): Promise<void> => {
      const sessionId = queryClient.getQueryData<BillPdfUploadSession | null>(
        queryKey
      )?.id
      if (!organizationId || !sessionId) {
        throw new Error("Bill PDF upload session is not loaded")
      }

      await updateMutation.mutateAsync({
        edit,
        organizationId,
        rowId,
        sessionId,
      })
    },
    // Source PDF links are short-lived signed URLs, so the viewer needs the
    // freshest session payload before it opens.
    refreshBillPdfUploadSessionFiles: async () => {
      if (isRowMutationInFlight()) {
        return queryResult.data?.files ?? []
      }

      const refreshed = await queryResult.refetch()
      return refreshed.data?.files ?? []
    },
    resetBillPdfUploadSessionMutations: () => {
      createMutation.reset()
      confirmMutation.reset()
      cancelMutation.reset()
      removeMutation.reset()
      updateMutation.reset()
    },
  }
}
